import {TKey} from '@/translations/i18n'
import {ObjectEntries, pickPropsFromObj} from '@/utilities/helpers'
import dayjs from '@/dayjs'

import {
    CareNetworkFieldsSchema,
    CloseProjectReportsSchema,
    FamilyAtmosphereFieldsSchema,
    MedicalReportAttributesSchema,
    MedicalReportFieldsSchema,
    MedicalReportValidationSchema,
    RenewProjectRehabilitationSchema,
    RenewProjectReportsSchema,
    StartProjectRehabilitationSchema,
    StartProjectReportsSchema,
    HousingNotesSchema,
    WithNullableAttachment
} from './formSchemas'

import {
    ClosingReason,
    CyclePeriodicityHypothesis,
    DisabilityExperienceProcessing,
    FamilySituation,
    InclusionLevel,
    MedicalReportAttributeKey,
    MedicalReportExtended,
    MedicalReportHouseType,
    MedicalReportJobType,
    MedicalReportQualification,
    MedicalReportSectionCopy,
    MedicalReportWorkingCondition,
    PredefinedText,
    SchoolType
} from './types'
import {
    MedicalReportGroup,
    MedicalReportType,
    MedicalReportTypeGroupKeyName,
    MedicalReportTypeKeyName,
    UploadedDocument
} from '@/types'
import {AccessType} from '../access-types/types'
import {ProjectSmall, ProjectTeamMember} from '../projects/types'
import {ProjectPlannedAccess} from '@/features/planned-accesses/types'
import {generatePath} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'

// map start project report keys to section labels
export const startProjectReportToSection = {
    diagnosis: {
        title: 'commons:diagnosis',
        description: 'medical_records:diagnosis_description'
    },
    clinicalUpdate: {
        title: 'medical_records:pathological_history',
        description: 'medical_records:pathological_history_description'
    },
    remotePathologicalHistory: {
        title: 'medical_records:remote_pathological_history',
        description: 'medical_records:remote_pathological_history_description'
    },
    familyHistory: {
        title: 'medical_records:family_history',
        description: 'medical_records:family_history_description'
    },
    physiologicalHistory: {
        title: 'medical_records:physiological_history',
        description: 'medical_records:physiological_history_description'
    },
    physicalExam: {
        title: 'medical_records:physical_exam',
        description: 'medical_records:physical_exam_description'
    },
    socioFactors: {
        title: 'medical_records:socio_factors',
        description: 'medical_records:socio_factors_description'
    },
    conclusions: {
        title: 'medical_records:conclusions',
        description: 'medical_records:conclusions_description'
    }
} as const satisfies Record<keyof StartProjectReportsSchema, MedicalReportSectionCopy>

// map start project rehabilitation keys to section labels
export const startProjectRehabilitationToSection = {
    treatmentsPlan: {
        title: 'medical_records:treatments_plan',
        description: 'medical_records:treatments_plan_description'
    },
    objectives: {
        title: 'commons:objectives',
        description: 'medical_records:objectives_description'
    }
} as const satisfies Record<keyof StartProjectRehabilitationSchema, MedicalReportSectionCopy>

// map close project report keys to section labels
export const closeProjectReportToSection = {
    diagnosis: {
        title: 'commons:diagnosis',
        description: 'medical_records:diagnosis_description'
    },
    clinicalUpdate: {
        title: 'medical_records:clinical_update',
        description: 'medical_records:clinical_update_description'
    },
    physicalExam: {
        title: 'medical_records:physical_exam',
        description: 'medical_records:physical_exam_description'
    },
    socioFactors: {
        title: 'medical_records:socio_factors',
        description: 'medical_records:socio_factors_description'
    },
    conclusions: {
        title: 'medical_records:conclusions',
        description: 'medical_records:objectives_description'
    }
} as const satisfies Record<keyof CloseProjectReportsSchema, MedicalReportSectionCopy>

export const renewProjectReportToSection = {
    diagnosis: {
        title: 'commons:diagnosis',
        description: 'medical_records:diagnosis_description'
    },
    clinicalUpdate: {
        title: 'medical_records:clinical_update',
        description: 'medical_records:clinical_update_description'
    },
    physicalExam: {
        title: 'medical_records:physical_exam',
        description: 'medical_records:physical_exam_description'
    },
    socioFactors: {
        title: 'medical_records:socio_factors',
        description: 'medical_records:socio_factors_description'
    },
    conclusions: {
        title: 'medical_records:conclusions',
        description: 'medical_records:objectives_description'
    }
} as const satisfies Record<keyof RenewProjectReportsSchema, MedicalReportSectionCopy>

export const renewRehabilitationProjectToSection = {
    treatmentsPlan: {
        title: 'medical_records:treatments_plan',
        description: 'medical_records:treatments_plan_description'
    },
    objectives: {
        title: 'commons:objectives',
        description: 'medical_records:objectives_description'
    }
} as const satisfies Record<keyof RenewProjectRehabilitationSchema, MedicalReportSectionCopy>

export const housingNotesMapToSections = {
    serviceDislocation: {
        title: 'medical_records:housing_notes_service_dislocation'
    },
    otherHousingNotes: {
        title: 'medical_records:housing_notes_other_housing_notes'
    }
} as const satisfies Record<keyof HousingNotesSchema, MedicalReportSectionCopy>

export const communicationAndDisabilityMapToLabel = {
    intraFamilyCommunicationDifficulty: 'medical_records:communication_difficulty_intra_family',
    extraFamilyCommunicationDifficulty: 'medical_records:communication_difficulty_extra_family',
    disabilityExperienceProcessing: 'medical_records:disability_experience_processing'
} as const satisfies Record<keyof FamilyAtmosphereFieldsSchema, TKey>

export const closeProjectReasonsMapToLabel = {
    achievement_of_objectives: 'medical_records:reason_achievement_of_objectives',
    patient_decision: 'medical_records:reason_patient_decision',
    transition_to_territorial_services: 'medical_records:reason_transition_to_territorial_services',
    transfer_to_another_center_or_facility: 'medical_records:reason_transfer_to_another_center_or_facility',
    transfer_to_city_region_or_another_state: 'medical_records:reason_transfer_to_city_region_or_another_state',
    therapist_shortage: 'medical_records:reason_therapist_shortage',
    organizational_or_scheduling_difficulties: 'medical_records:reason_organizational_or_scheduling_difficulties',
    lack_of_authorization_from_asl: 'medical_records:reason_lack_of_authorization_from_asl',
    patient_death: 'medical_records:reason_patient_death',
    other: 'commons:other'
} as const satisfies Record<ClosingReason, TKey>

export const periodicityHypothesisMapToLabel = {
    once_year: 'medical_records:hypothesis_once_year',
    twice_year: 'medical_records:hypothesis_twice_year',
    single_non_repeatable_cycle: 'medical_records:hypothesis_single_cycle'
} as const satisfies Record<CyclePeriodicityHypothesis, TKey>

export const extensionDaysMapToLabel = {
    '60': 'commons:days_count',
    '90': 'commons:days_count',
    '120': 'commons:days_count',
    '180': 'commons:days_count',
    '365': 'commons:days_count'
} as const satisfies Record<60 | 90 | 120 | 180 | 365, TKey>

export const additionalPredefinedTextMapToLabel = {
    text_1: 'medical_records:text_number',
    text_2: 'medical_records:text_number',
    text_3: 'medical_records:text_number'
} as const satisfies Record<PredefinedText, TKey>

export const additionalPredefinedTextMapToDescription = {
    text_1: 'medical_records:additional_predefined_text_1',
    text_2: 'medical_records:additional_predefined_text_2',
    text_3: 'medical_records:additional_predefined_text_3'
} as const satisfies Record<PredefinedText, TKey>

export const attechedClinicalReportMapToLabel = {
    therapistEvaluation: 'medical_records:therapist_evaluation',
    teamEvaluation: 'medical_records:team_evaluation',
    medicalEvaluation: 'medical_records:medical_evaluation'
} as const satisfies Record<MedicalReportGroup, TKey>

export const qualificationMapToLabel = {
    none: 'medical_records:qualification_none',
    elementary_license: 'medical_records:qualification_elementary_license',
    lower_secondary_license: 'medical_records:qualification_lower_secondary_license',
    upper_secondary_school: 'medical_records:qualification_upper_secondary_school',
    degree_or_higher: 'medical_records:qualification_degree_or_higher'
} as const satisfies Record<MedicalReportQualification, TKey>

export const workingConditionMapToLabel = {
    student: 'medical_records:working_condition_student',
    unemployed: 'medical_records:working_condition_unemployed',
    jobless: 'medical_records:working_condition_jobless',
    temporary_laid_off: 'medical_records:working_condition_temporary_laid_off',
    employed_permanent_contract: 'medical_records:working_condition_employed_permanent_contract',
    employed_collaboration_contract: 'medical_records:working_condition_employed_collaboration_contract',
    employed_other: 'medical_records:working_condition_other_employed',
    retired_seniority_pension: 'medical_records:working_condition_retired_seniority_pension',
    retired_social_pension: 'medical_records:working_condition_retired_social_pension',
    retired_disability_pension: 'medical_records:working_condition_retired_disability_pension',
    retired_other: 'medical_records:working_condition_other_retired'
} as const satisfies Record<MedicalReportWorkingCondition, TKey>

export const medicalReportJobTypeToLabel = {
    manual: 'medical_records:job_type_manual',
    knowledge_work: 'medical_records:job_type_knowledge_work'
} as const satisfies Record<MedicalReportJobType, TKey>

export const disabilityBenefitsMapToLabel = {
    civilDisability: 'medical_records:disability_benefit_civil_disability',
    companionAllowance: 'medical_records:disability_benefit_companion_allowance',
    attendanceAllowance: 'medical_records:disability_benefit_attendance_allowance',
    law104Article3Paragraph1: 'medical_records:disability_benefit_law_104_article_3_paragraph_1',
    law104Article3Paragraph3: 'medical_records:disability_benefit_law_104_article_3_paragraph_3',
    disabilityPercentage: 'medical_records:disability_review_disability_percentage',
    companionAllowanceReviewable: 'medical_records:disability_review_companion_allowance_reviewable',
    attendanceAllowanceReviewable: 'medical_records:disability_review_attendance_allowance_reviewable'
} as const satisfies Record<string, TKey>

export const housingConditionMapToLabel = {
    owned_home: 'medical_records:housing_condition_owned_home',
    rented_home: 'medical_records:housing_condition_rented_home',
    public_housing_assignment: 'medical_records:housing_condition_public_housing_assignment',
    occupied_home: 'medical_records:housing_condition_occupied_home',
    eviction_condition: 'medical_records:housing_condition_eviction_condition',
    free_use_loan: 'medical_records:housing_condition_free_use_loan',
    other: 'commons:other',
    enter_text: 'medical_records:housing_condition_enter_text'
} as const satisfies Record<MedicalReportHouseType, TKey>

export const disabilityExperienceProcessingMapToLabel = {
    processing_none: 'medical_records:disability_experience_processing_none',
    processing_low: 'medical_records:disability_experience_processing_low',
    processing_medium: 'medical_records:disability_experience_processing_medium',
    processing_good: 'medical_records:disability_experience_processing_good'
} as const satisfies Record<DisabilityExperienceProcessing, TKey>

export const careNetworkMapToLabel = {
    volunteerNetwork: 'medical_records:social_services_volunteer_network',
    volunteerNetworkDetails: 'medical_records:social_services_volunteer_network_details',
    paidNetwork: 'medical_records:social_services_paid_network',
    paidNetworkDetails: 'medical_records:social_services_paid_network_details',
    activatedTerritorialServices: 'medical_records:social_services_activated_territorial_services',
    municipalTerritorialServices: 'medical_records:social_services_municipal_territorial_services',
    aslTerritorialServices: 'medical_records:social_services_asl_territorial_services',
    homeCare: 'medical_records:social_services_home_care',
    homeCareNotes: 'medical_records:social_services_home_care_notes',
    transport: 'medical_records:social_services_transport',
    transportNotes: 'medical_records:social_services_transport_notes',
    dayCenters: 'medical_records:social_services_day_centers',
    dayCentersNotes: 'medical_records:social_services_day_centers_notes',
    associations: 'medical_records:social_services_associations',
    associationsNotes: 'medical_records:social_services_associations_notes',
    otherServices: 'medical_records:social_services_other_services',
    otherServicesNotes: 'medical_records:social_services_other_services_notes'
} as const satisfies Record<keyof CareNetworkFieldsSchema, TKey>

export const schoolTypeMapToLabel = {
    none: 'medical_records:school_type_none',
    nursery: 'medical_records:school_type_nursery',
    kindergarten: 'medical_records:school_type_kindergarten',
    primary: 'medical_records:school_type_primary',
    middle: 'medical_records:school_type_middle',
    high: 'medical_records:school_type_high',
    training: 'medical_records:school_type_training',
    socio_work: 'medical_records:school_type_socio_work'
} as const satisfies Record<SchoolType, TKey>

// Define the inclusion level options
export const inclusionLevelMapToLabel = {
    low: 'medical_records:inclusion_level_low',
    medium: 'medical_records:inclusion_level_medium',
    good: 'medical_records:inclusion_level_good',
    excellent: 'medical_records:inclusion_level_excellent'
} as const satisfies Record<InclusionLevel, TKey>

export const familySituationMinorsMapToLabel = {
    single_parent_working: 'medical_records:single_parent_working',
    unemployed_parents: 'medical_records:unemployed_parents',
    part_time_work: 'medical_records:part_time_work',
    full_time_work: 'medical_records:full_time_work',
    siblings: 'medical_records:siblings',
    siblings_with_social_services: 'medical_records:siblings_with_social_services',
    single_parent_family: 'medical_records:single_parent_family',
    separated_parents: 'medical_records:separated_parents',
    conflictual_separated_parents: 'medical_records:conflictual_separated_parents',
    legal_guardian: 'medical_records:legal_guardian',
    social_services_guardianship: 'medical_records:social_services_guardianship',
    precarious_economic_conditions: 'medical_records:precarious_economic_conditions'
} as const satisfies Record<FamilySituation, TKey>

export const CARE_NETWORK_SWITCHES = pickPropsFromObj(careNetworkMapToLabel, [
    'volunteerNetwork',
    'paidNetwork',
    'activatedTerritorialServices',
    'homeCare',
    'transport',
    'dayCenters',
    'associations',
    'otherServices'
])

export const isSingleNoteReport = (group: MedicalReportTypeGroupKeyName, type: MedicalReportTypeKeyName): boolean => {
    const groups = new Set<MedicalReportTypeGroupKeyName>([
        'diaries',
        'medical_reports',
        'evaluations',
        'specialist_consultations'
    ])
    const includedTypesFromExcludedGroups = new Set<MedicalReportTypeKeyName>(['initial_project_assessment'])
    const excludedTypesFromIncludedGroups = new Set<MedicalReportTypeKeyName>([
        'glo',
        'team_report',
        'start_continuous_project',
        'close_continuous_project',
        'start_cyclical_project',
        'close_cyclical_project',
        'renew_continuous_project',
        'continuous_project_extension_request'
    ])

    if (excludedTypesFromIncludedGroups.has(type)) return false
    if (includedTypesFromExcludedGroups.has(type)) return true
    if (groups.has(group)) return true

    return false
}

export const getAttributesFromValues = (
    formValues: Partial<WithNullableAttachment<MedicalReportFieldsSchema>>
): Partial<MedicalReportValidationSchema> => {
    const reports = []
    for (const [key, value] of Object.entries(formValues) as [keyof typeof formValues, unknown][]) {
        // Only include attributes that have a value and are valid attribute keys
        if (value != undefined && value != '' && MedicalReportAttributeKey.safeParse(key).success) {
            reports.push([key, value])
        }
    }

    return Object.fromEntries(reports)
}

export const remapMedicalReportAttributesToDefaultValues = (
    attributes: MedicalReportExtended['medicalRecordReportAttributes'],
    initialPlannedAccesses?: ProjectPlannedAccess
) => {
    const value = attributes.reduce((acc, attribute) => {
        if (attribute.name == 'newProjectStartDate' || attribute.name == 'reopeningNextCycle') {
            return {
                ...acc,
                [attribute.name]: new Date(attribute.value)
            }
        }

        if (
            attribute.name == 'medicalEvaluation' ||
            attribute.name == 'therapistEvaluation' ||
            attribute.name == 'teamEvaluation'
        ) {
            return {
                ...acc,
                [attribute.name]: attribute.value
            }
        }

        if (attribute.name == 'newProjectDurationDays') {
            return {
                ...acc,
                [attribute.name]: parseInt(attribute.value, 10)
            }
        }

        if (attribute.name == 'plannedAccesses') {
            const plannedAccesses = attribute.object as {
                accessType?: AccessType
                numberOfAccesses?: number
                frequency: 'monthly' | 'weekly'
            }[]

            const inPersonAccesses = plannedAccesses
                .filter(access => access.accessType?.groupName == 'treatments')
                .map(access => ({
                    accessType: access.accessType,
                    frequency: access.frequency as 'monthly' | 'weekly',
                    numberOfAccesses: parseInt(access.numberOfAccesses?.toString() ?? '0', 10)
                }))
            const singleAccesses = plannedAccesses
                .filter(access => access.accessType?.groupName == 'in_person_accesses')
                .map(access => ({
                    accessType: access.accessType,
                    numberOfAccesses: parseInt(access.numberOfAccesses?.toString() ?? '0', 10)
                }))

            const absenceAccesses = plannedAccesses
                .filter(access => access.accessType?.groupName == 'non_in_person_accesses')
                .map(access => ({
                    accessType: access.accessType,
                    numberOfAccesses: parseInt(access.numberOfAccesses?.toString() ?? '0', 10)
                }))

            return {
                ...acc,
                plannedAccesses: {
                    inPersonAccesses,
                    singleAccesses,
                    absenceAccesses
                }
            }
        }

        if (attribute.name == 'treatmentsPlan') {
            return {
                ...acc,
                treatmentsPlan: attribute?.value || initialPlannedAccesses?.treatmentPlan || ''
            }
        }

        if (attribute.name == 'objectives') {
            return {
                ...acc,
                objectives: attribute?.value || initialPlannedAccesses?.targets || ''
            }
        }

        if (attribute.name == 'teamMembers') {
            return {
                ...acc,
                teamMembers: attribute.object as ProjectTeamMember[]
            }
        }

        if (attribute.name == 'documentAttachment') {
            return {
                ...acc,
                documentAttachment: attribute.object?.[0] as UploadedDocument
            }
        }

        if (attribute.name == 'documentDate') {
            return {
                ...acc,
                [attribute.name]: new Date(attribute.value)
            }
        }

        if (attribute.name == 'companionAllowance') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'extraFamilyCommunicationDifficulty') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'transport') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'homeCare') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'dayCenters') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'civilDisability') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'otherServices') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'hasArchitecturalBarriers') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'paidNetwork') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'volunteerNetwork') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'associations') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'activatedTerritorialServices') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        if (attribute.name == 'intraFamilyCommunicationDifficulty') {
            return {
                ...acc,
                [attribute.name]: attribute.value == 'true'
            }
        }

        return {
            ...acc,
            [attribute.name]: attribute.value
        }
    }, {} as Partial<MedicalReportFieldsSchema>)

    return value
}

interface RemappedValue {
    name: MedicalReportAttributeKey
    value: unknown
    object?: Record<string, unknown>[]
}

export const remapMedicalReportAttributesToPayload = (
    values: Partial<
        WithNullableAttachment<
            Omit<MedicalReportAttributesSchema, 'isReportAccessDetatched' | 'noProject' | 'isMixedProject'>
        >
    >
) => {
    const remappedValues = ObjectEntries(values)
        .flatMap((entry): RemappedValue[] => {
            if (!entry) {
                throw new Error('medical report attribute entry cannot be nullish')
            }

            const [key, value] = entry

            if (key == 'teamMembers') {
                return [
                    {
                        name: key,
                        value: 'obj',
                        object: value
                    }
                ]
            }

            if (key == 'plannedAccesses') {
                const treatments = value?.inPersonAccesses.map(access => ({
                    accessType: access.accessType,
                    numberOfAccesses: access.numberOfAccesses?.toString(),
                    frequency: access.frequency
                }))

                const singleAccesses = value?.singleAccesses.map(access => ({
                    accessType: access.accessType,
                    numberOfAccesses: access.numberOfAccesses?.toString()
                }))

                const absenceAccesses = value?.absenceAccesses.map(access => ({
                    accessType: access.accessType,
                    numberOfAccesses: access.numberOfAccesses?.toString()
                }))

                const plannedAccesses = [...(treatments || []), ...(singleAccesses || []), ...(absenceAccesses || [])]

                return [
                    {
                        name: key,
                        value: 'obj',
                        object: plannedAccesses
                    }
                ]
            }

            if (key == 'reopeningNextCycle' || (key == 'newProjectStartDate' && value instanceof Date)) {
                return [
                    {
                        name: key,
                        value: value && dayjs(value).format('YYYY-MM-DD')
                    }
                ]
            }

            if (key == 'newProjectDurationDays') {
                return [
                    {
                        name: key,
                        value: value ? value.toString() : undefined
                    }
                ]
            }

            if (key == 'familyMembersSituations') {
                return [
                    {
                        name: key,
                        value: value ? JSON.stringify(value) : undefined
                    }
                ]
            }

            if (key == 'disabilityPercentage') {
                return [
                    {
                        name: key,
                        value: value ? value.toString() : undefined
                    }
                ]
            }

            if (key == 'isAdditionalPredefinedText2') {
                return [
                    {
                        name: key,
                        value: value ? value.toString() : undefined
                    }
                ]
            }

            if (key == 'medicalEvaluation' || key == 'therapistEvaluation' || key == 'teamEvaluation') {
                return [
                    {
                        name: key,
                        value: typeof value == 'string' ? value : value?.id ?? undefined
                    }
                ]
            }

            if (key == 'documentAttachment' && value) {
                return [
                    {
                        name: key,
                        value: 'obj',
                        object: [value]
                    }
                ]
            }

            return [
                {
                    name: key,
                    value: value ? value.toString() : undefined
                }
            ]
        })
        .filter(attribute => !!attribute.value)

    return remappedValues
}

/**
 * Report types that only make sense within a project: they describe the project lifecycle
 * (opening, closing, renewal, extension) or its assessments. When the report being compiled
 * has no project attached, these types must not be selectable.
 */
export const PROJECT_DEPENDENT_REPORT_TYPES: readonly MedicalReportTypeKeyName[] = [
    'start_continuous_project',
    'start_cyclical_project',
    'close_continuous_project',
    'close_cyclical_project',
    'team_report',
    'glo',
    'initial_project_assessment',
    'intermediate_project_assessment',
    'continuous_project_extension_request',
    'renew_continuous_project'
]

/**
 * Value used to clear the report type. The field is emptied rather than unset because the form
 * keeps it as an object from the very first render, and several consumers read `reportType.id` /
 * `reportType.keyName` without guarding against a missing object.
 */
export const EMPTY_MEDICAL_REPORT_TYPE = {} as MedicalReportType

/**
 * Whether a report type can be used with the given project.
 *
 * Two rules apply: project-bound types need a project at all, and some of them additionally
 * require a specific project type. Shared by the report type select (which disables the
 * non-selectable types) and by the form status hook (which blocks the submit).
 */
export const isReportTypeProjectCompliant = (
    reportTypeKeyName: MedicalReportTypeKeyName | undefined,
    project: Pick<ProjectSmall, 'id' | 'type' | 'transformedProjectId' | 'renewedProjectId'> | null | undefined
): boolean => {
    if (!reportTypeKeyName) return true

    // The form may hold an empty object when no project is attached, so check the id
    if (!project?.id) {
        return !PROJECT_DEPENDENT_REPORT_TYPES.includes(reportTypeKeyName)
    }

    // With a project attached, some types are still incompatible with the project type
    const nonCompliantCases: Partial<Record<MedicalReportTypeKeyName, boolean>> = {
        start_continuous_project: project.type == 'cyclic',
        // A cyclic project accepts a renewal report only if it was transformed or extended
        // (for a cyclic project, `renewedProjectId` denotes an extension).
        renew_continuous_project:
            project.type == 'cyclic' && !project.transformedProjectId && !project.renewedProjectId,
        close_continuous_project: project.type == 'cyclic',
        start_cyclical_project: project.type == 'continuous',
        close_cyclical_project: project.type == 'continuous'
    }

    return !nonCompliantCases[reportTypeKeyName]
}

export const medicalReportKeyMapToLabel = {
    cardiology_visit: 'medical_records:type_cardiology_visit',
    certification_l104: 'medical_records:type_certification_l104',
    close_continuous_project: 'medical_records:type_close_continuous_project',
    close_cyclical_project: 'medical_records:type_close_cyclical_project',
    cognitive_neuropsychological_treatment: 'medical_records:type_cognitive_neuropsychological_treatment',
    continuous_project_extension_request: 'medical_records:type_continuous_project_extension_request',
    counseling: 'medical_records:type_counseling',
    document: 'medical_records:type_document',
    educational_intervention_treatment: 'medical_records:type_educational_intervention_treatment',
    glo: 'medical_records:type_glo',
    initial_project_assessment: 'medical_records:type_initial_project_assessment',
    initial_social_service_evaluation: 'medical_records:type_initial_social_service_evaluation',
    intermediate_project_assessment: 'medical_records:type_intermediate_project_assessment',
    neurological_specialist_visit: 'medical_records:type_neurological_specialist_visit',
    neuromotor_evaluation: 'medical_records:type_neuromotor_evaluation',
    neuromotor_treatment: 'medical_records:type_neuromotor_treatment',
    neuropsychiatric_specialist_visit: 'medical_records:type_neuropsychiatric_specialist_visit',
    nutritional_visit: 'medical_records:type_nutritional_visit',
    occupational_therapist_evaluation: 'medical_records:type_occupational_therapist_evaluation',
    occupational_therapy_treatment: 'medical_records:type_occupational_therapy_treatment',
    orl_visit: 'medical_records:type_orl_visit',
    orthopedic_visit: 'medical_records:type_orthopedic_visit',
    other: 'commons:other',
    parent_training: 'medical_records:type_parent_training',
    pediatric_visit: 'medical_records:type_pediatric_visit',
    physiatric_specialist_visit: 'medical_records:type_physiatric_specialist_visit',
    physiotherapist_evaluation: 'medical_records:type_physiotherapist_evaluation',
    physiotherapy_treatment: 'medical_records:type_physiotherapy_treatment',
    previous_extensions: 'medical_records:type_previous_extensions',
    professional_educator_evaluation: 'medical_records:type_professional_educator_evaluation',
    psychologist_evaluation: 'medical_records:type_psychological_evaluation',
    psychology_treatment: 'medical_records:type_psychological_treatment',
    psychomotor_evaluation: 'medical_records:type_psychomotor_evaluation',
    psychomotor_treatment: 'medical_records:type_psychomotor_treatment',
    pulmonary_visit: 'medical_records:type_pulmonology_visit',
    renew_continuous_project: 'medical_records:type_renew_continuous_project',
    social_service_evaluation: 'medical_records:type_social_service_evaluation',
    speech_therapist_evaluation: 'medical_records:type_speech_therapist_evaluation',
    speech_therapy_treatment: 'medical_records:type_speech_therapy_treatment',
    start_continuous_project: 'medical_records:type_start_continuous_project',
    start_cyclical_project: 'medical_records:type_start_cyclical_project',
    team_report: 'medical_records:type_team_report',
    renew_close_rehab_project: 'medical_records:type_renew_close_rehab_project',
    update_medical_record: 'medical_records:type_update_medical_record',
    medical_interview: 'medical_records:type_medical_interview',
    therapy_evaluation: 'medical_records:type_therapy_evaluation',
    cardiac_holter: 'medical_records:type_cardiac_holter'
} as const satisfies Record<MedicalReportTypeKeyName, TKey>

/**
 * Generate path to create medical report page with appropriate search parameters
 */
export const generateCreateMedicalReportPath = (
    patientId: string,
    projectId?: string | null,
    medicalReportType?: MedicalReportType | null,
    accessIds?: string[] | null,
    maxDate?: Date | null
): string | undefined => {
    if (!projectId) {
        return undefined
    }

    // Construct URL search params for navigation
    const createSearchParams = () => {
        const params = new URLSearchParams()

        if (projectId) {
            params.append('projectId', projectId)
        }

        if (medicalReportType) {
            params.append('medicalReportTypeId', medicalReportType.id)
            params.append('medicalReportTypeKeyName', medicalReportType.keyName)
            params.append('medicalReportTypeName', medicalReportType.name)
            params.append('medicalReportTypeGroupName', medicalReportType.groupName)
            params.append('medicalReportTypeGroupKeyName', medicalReportType.groupKeyName)
        }

        if (accessIds) {
            accessIds.forEach(accessId => {
                params.append('accessIds', accessId)
            })
        }

        if (maxDate) {
            params.append(
                'date',
                maxDate < new Date() ? dayjs(maxDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
            )
        }

        const searchString = params.toString()
        return searchString ? `?${searchString}` : ''
    }

    const path = generatePath(routes.CREATE_MEDICAL_REPORT.path, {id: patientId})
    return `${path}${createSearchParams()}`
}
